
import React, { useState, useEffect } from "react";
import UploadDocument from "./UploadDocument";
import { auth } from '../../firebase';
import { useNavigate } from "react-router-dom";
import "../../css/ImportDocument.css"
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import "../../css/HomePage.css";


function ImportDocument() {
    const [showUpload, setShowUpload] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [uid, setUid] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [authResolved, setAuthResolved] = useState(false);
    const navigate = useNavigate();
    const { enhancedRightClickEnabled } = useQuickLinks();

    // Theme cleanup on unmount



    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUid(user.uid);
                setUserEmail(user.email || "");
            } else {
                setUid("");
                setUserEmail("");
            }
            setAuthResolved(true);
        });
        return () => unsubscribe();
    }, []);

    const handleArrowClick = () => {
        setIsTransitioning(true);
        setTimeout(() => {
            setShowUpload(true);
        }, 150);
    };

  const loggedOutOptions = [
    {
      label: "Login",
      icon: <i className="fas fa-sign-in-alt"></i>,
      url: "/login",
      onClick: () => navigate("/login"),
    },
    {
      label: "Register",
      icon: <i className="fas fa-user-plus"></i>,
      url: "/register",
      onClick: () => navigate("/register"),
    },
    {
      label: "Try My Notebook",
      icon: <i className="fas fa-book"></i>,
      url: "/my-notebook",
      onClick: () => navigate("/my-notebook"),
    },
    {
      label: "Visit YouTube Channel",
      icon: <i className="fab fa-youtube"></i>,
      url: "https://www.youtube.com/@schoolofintology4062",
      onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer"),
    },
    {
      label: "Join Discord",
      icon: <i className="fab fa-discord"></i>,
      url: "https://discord.gg/DYkESHwgVA",
      onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer"),
    },
  ];

const loggedInOptions = [
      {
        label: "Home",
        icon: <i className="fas fa-home"></i>,
        url: "/",
        onClick: () => navigate("/"),
      },
  {
    label: "Your Documents",
    url: "/documentation",
    onClick: () => navigate("/documentation"),
  },
  {
    label: "Your Walkthroughs",
    url: "/walkthroughs",
    onClick: () => navigate("/walkthroughs"),
  },
  {
    label: "Your Notebook",
    url: "/my-notebook",
    onClick: () => navigate("/my-notebook"),
  },
  {
    label: "Your Workspace",
    url: "/workspaces",
    onClick: () => navigate("/workspaces"),
  },
  {
    label: "Account Settings",
    url: `/account/${uid}`,
    onClick: () => navigate(`/account/${uid}`),
  },
];

  // Use loggedInOptions if signed in, otherwise loggedOutOptions
  const menuOptions = uid ? loggedInOptions : loggedOutOptions;
  const { showContextMenu, ContextMenuComponent } = useContextMenu(menuOptions);

    const handleBackFromUpload = () => {
        setShowUpload(false);
        setIsTransitioning(false);
    };

    if (!authResolved) {
            return (
                <>
                    <div className="loading-animation-div" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
                        <div className="coffee-cup-loader">
                            <div className="cup">
                                <div className="cup-body"></div>
                                <div className="cup-handle"></div>
                                <div className="cup-base"></div>
                                <div className="cup-shadow"></div>
                                <div className="steam steam1"></div>
                                <div className="steam steam2"></div>
                                <div className="steam steam3"></div>
                            </div>
                        </div>
                        <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>Checking authentication...</p>
                    </div>
                </>
            );
    }

    if (!uid) {
        return (
            <div 
                className="DocumentationMainPage__div"
            onContextMenu={
                enhancedRightClickEnabled
                ? (e) => {
                    e.preventDefault();
                    showContextMenu(e.clientX, e.clientY);
                    }
                : undefined
            }
            >
                <div className="DocumentationMainPage__main-content">
                    <p className="large">It looks like you're not signed in.</p>
                    <p className="medium">
                        <strong>To import documents, you must have an account with us</strong>. If you already have an account, 
                        you can login by clicking the Login button below. If you don't already have an account, you can create a new 
                        one by clicking the Register button below.
                    </p>
                    <button onClick={() => navigate('/')} style={{ marginRight: "10px" }} className="ConflictResolver__button">Home</button>
                    <button onClick={() => navigate('/login')} style={{ marginRight: "10px" }} className="ConflictResolver__button">Login</button>
                    <button onClick={() => navigate('/register')} className="ConflictResolver__button">Register</button>
                </div>
                {enhancedRightClickEnabled && ContextMenuComponent}
            </div>
        );
    }

    if (showUpload) {
        return (
            <UploadDocument 
                onBack={handleBackFromUpload}
                enhancedRightClickEnabled={enhancedRightClickEnabled}
                showContextMenu={showContextMenu}
                ContextMenuComponent={ContextMenuComponent}
            />
        );
    }

    return (
        <div 
            className={`ImportDocument__main-div ${isTransitioning ? 'transitioning' : ''}`}
            onContextMenu={
                enhancedRightClickEnabled
                ? (e) => {
                    e.preventDefault();
                    showContextMenu(e.clientX, e.clientY);
                    }
                : undefined
            }
        >
            <div className="ImportDocument__content">
                <h2>Let's Import Your Document...</h2>

                <p className="medium" style={{ marginTop: "20px" }}>
                    Thank you for choosing Bluebird Documentation to host your documents! This process 
                    is to import a single document from another documentation platform.
                </p>

                <p className="small">
                    We'll try to make instructions as clear as possible. If you're facing issues, need further guidance, 
                    or have encountered a bug, please feel free to submit a support ticket.
                </p>

                <p className="medium semi-bold">
                    Below is a general overview of the process.
                </p>

                <ol>
                    <li>Click the arrow on the right of the page to begin.</li>
                    <li>Select the HTML or Markdown (.md) file you have exported from your other platform.</li>
                    <li>After we've parsed the document, you'll get the chance to review it and make any necessary changes.</li>
                    <li>Lastly, set document permissions like you would any other document on this platform.</li>
                </ol>

                <p className="small">
                    This is mostly a work in progress, and there are likely to be issues. We really appreciate you're trying 
                    this feature, and please feel free to submit feedback, so we can continue to improve this feature!
                </p>

            </div>

            <div 
                className="ImportDocument__arrow" 
                onClick={handleArrowClick}
                title="Click to continue"
                aria-label="Continue to next step"
            >
                ❯
            </div>

            {enhancedRightClickEnabled && ContextMenuComponent}

        </div>
    )
}

export default ImportDocument